import { parseDollarsInput, type PriceKey } from '@auctisync/api-client';
import type { Tables, TablesUpdate } from '@auctisync/shared';

/**
 * The lot grid: every lot in a sale on one screen, every cell editable.
 *
 * Jim, 2026-10-06, reviewing his first test sale: "part of the auction admin
 * build is supposed to be a spreadsheet view of the lots. Going through lot
 * page by lot page is too slow." The grid opens in a window of its own so a
 * second monitor can hold it beside the photos.
 *
 * This file is the grid's RULES, with no React in them: which columns exist,
 * what a cell shows, what a typed cell means, what may not be edited, and the
 * patch a row's edits become. The page renders them. Keeping them here is how
 * they get tested — a spreadsheet of money fields is not a thing to prove by
 * clicking.
 *
 * 🔴 WHAT MAY NOT BE EDITED, AND WHY (`lockedReason`):
 *   * The STARTING BID of a lot with bids. It sets the whole increment ladder;
 *     moving it under live bidding can invalidate bids already placed. The
 *     real-world fix for a wrong starting bid is to cancel the lot and relist
 *     it. (The Copilot already refuses this; the office lot page does NOT —
 *     a gap raised with Jim, not closed here.)
 *   * The LOT NUMBER and SALE ORDER of a lot with bids. Sale order decides the
 *     close time through the sale's stagger, so changing it moves the clock on
 *     people who are already bidding.
 * The reserve is NOT locked: lowering a reserve to let something sell is an
 * ordinary auction move. The page confirms it when the lot is live.
 */

export type LotRow = Tables<'lots'>;

export type GridGroup =
  | 'identity'
  | 'pricing'
  | 'listing'
  | 'specs'
  | 'identifiers'
  | 'location'
  | 'status'
  | 'live';

export type GridKind = 'int' | 'text' | 'longtext' | 'money' | 'bool' | 'select' | 'readonly';

export interface GridColumn {
  key: string;
  label: string;
  group: GridGroup;
  kind: GridKind;
  /** The lot column it writes, or the `custom_fields` key for a spec. */
  target: { column: keyof LotRow } | { customField: string };
  /** On screen before anyone touches the Columns menu. */
  defaultVisible: boolean;
  /** Pixels — a spreadsheet needs steady columns, not ones that jump on typing. */
  width: number;
}

/** The lot statuses the office may set by hand (as on the lot page). */
export const GRID_LOT_STATUSES = [
  'draft',
  'scheduled',
  'active',
  'sold',
  'unsold',
  'no_sale',
  'cancelled',
] as const;

export const LOT_GRID_COLUMNS: readonly GridColumn[] = [
  {
    key: 'lot_number',
    label: 'Lot #',
    group: 'identity',
    kind: 'int',
    target: { column: 'lot_number' },
    defaultVisible: true,
    width: 70,
  },
  {
    key: 'sale_order',
    label: 'Order',
    group: 'identity',
    kind: 'int',
    target: { column: 'sale_order' },
    defaultVisible: true,
    width: 70,
  },
  {
    key: 'title',
    label: 'Title',
    group: 'identity',
    kind: 'text',
    target: { column: 'title' },
    defaultVisible: true,
    width: 280,
  },
  {
    key: 'condition',
    label: 'Condition',
    group: 'identity',
    kind: 'text',
    target: { column: 'condition' },
    defaultVisible: true,
    width: 120,
  },
  {
    key: 'quantity',
    label: 'Qty',
    group: 'identity',
    kind: 'int',
    target: { column: 'quantity' },
    defaultVisible: true,
    width: 60,
  },
  {
    key: 'category_id',
    label: 'Category',
    group: 'identity',
    kind: 'select',
    target: { column: 'category_id' },
    defaultVisible: true,
    width: 150,
  },
  {
    key: 'consignor_id',
    label: 'Consignor',
    group: 'identity',
    kind: 'select',
    target: { column: 'consignor_id' },
    defaultVisible: true,
    width: 150,
  },
  {
    key: 'control_number',
    label: 'Control #',
    group: 'identity',
    kind: 'text',
    target: { column: 'control_number' },
    defaultVisible: false,
    width: 110,
  },
  {
    key: 'description',
    label: 'Description',
    group: 'identity',
    kind: 'longtext',
    target: { column: 'description' },
    defaultVisible: false,
    width: 260,
  },

  {
    key: 'msrp_cents',
    label: 'MSRP',
    group: 'pricing',
    kind: 'money',
    target: { column: 'msrp_cents' },
    defaultVisible: true,
    width: 100,
  },
  {
    key: 'retail_price_cents',
    label: 'Retail',
    group: 'pricing',
    kind: 'money',
    target: { column: 'retail_price_cents' },
    defaultVisible: true,
    width: 100,
  },
  {
    key: 'starting_bid_cents',
    label: 'Starting bid',
    group: 'pricing',
    kind: 'money',
    target: { column: 'starting_bid_cents' },
    defaultVisible: true,
    width: 110,
  },
  {
    key: 'reserve_cents',
    label: 'Reserve',
    group: 'pricing',
    kind: 'money',
    target: { column: 'reserve_cents' },
    defaultVisible: true,
    width: 100,
  },
  {
    key: 'estimate_min_cents',
    label: 'Est. low',
    group: 'pricing',
    kind: 'money',
    target: { column: 'estimate_min_cents' },
    defaultVisible: true,
    width: 100,
  },
  {
    key: 'estimate_max_cents',
    label: 'Est. high',
    group: 'pricing',
    kind: 'money',
    target: { column: 'estimate_max_cents' },
    defaultVisible: true,
    width: 100,
  },
  {
    key: 'bid_increment_scale_id',
    label: 'Increments',
    group: 'pricing',
    kind: 'select',
    target: { column: 'bid_increment_scale_id' },
    defaultVisible: false,
    width: 150,
  },

  {
    key: 'show_msrp',
    label: 'Show MSRP',
    group: 'listing',
    kind: 'bool',
    target: { column: 'show_msrp' },
    defaultVisible: true,
    width: 90,
  },
  {
    key: 'show_retail_price',
    label: 'Show retail',
    group: 'listing',
    kind: 'bool',
    target: { column: 'show_retail_price' },
    defaultVisible: true,
    width: 90,
  },
  {
    key: 'show_estimate',
    label: 'Show est.',
    group: 'listing',
    kind: 'bool',
    target: { column: 'show_estimate' },
    defaultVisible: true,
    width: 90,
  },

  {
    key: 'brand',
    label: 'Brand',
    group: 'specs',
    kind: 'text',
    target: { customField: 'brand' },
    defaultVisible: true,
    width: 130,
  },
  {
    key: 'model',
    label: 'Model',
    group: 'specs',
    kind: 'text',
    target: { customField: 'model' },
    defaultVisible: true,
    width: 130,
  },
  {
    key: 'year',
    label: 'Year',
    group: 'specs',
    kind: 'text',
    target: { customField: 'year' },
    defaultVisible: true,
    width: 80,
  },
  {
    key: 'material',
    label: 'Material',
    group: 'specs',
    kind: 'text',
    target: { customField: 'material' },
    defaultVisible: true,
    width: 120,
  },
  {
    key: 'color',
    label: 'Colour',
    group: 'specs',
    kind: 'text',
    target: { customField: 'color' },
    defaultVisible: true,
    width: 110,
  },
  {
    key: 'dimensions',
    label: 'Dimensions',
    group: 'specs',
    kind: 'text',
    target: { customField: 'dimensions' },
    defaultVisible: true,
    width: 140,
  },

  {
    key: 'mpn',
    label: 'MPN',
    group: 'identifiers',
    kind: 'text',
    target: { column: 'mpn' },
    defaultVisible: false,
    width: 120,
  },
  {
    key: 'upc',
    label: 'UPC',
    group: 'identifiers',
    kind: 'text',
    target: { column: 'upc' },
    defaultVisible: false,
    width: 130,
  },
  {
    key: 'sku',
    label: 'SKU',
    group: 'identifiers',
    kind: 'text',
    target: { column: 'sku' },
    defaultVisible: false,
    width: 120,
  },

  {
    key: 'location_bin',
    label: 'Bin',
    group: 'location',
    kind: 'text',
    target: { column: 'location_bin' },
    defaultVisible: false,
    width: 100,
  },
  {
    key: 'location_notes',
    label: 'Location notes',
    group: 'location',
    kind: 'text',
    target: { column: 'location_notes' },
    defaultVisible: false,
    width: 180,
  },
  {
    key: 'consignment_profile_id',
    label: 'Consignment terms',
    group: 'location',
    kind: 'select',
    target: { column: 'consignment_profile_id' },
    defaultVisible: false,
    width: 170,
  },

  {
    key: 'status',
    label: 'Status',
    group: 'status',
    kind: 'select',
    target: { column: 'status' },
    defaultVisible: true,
    width: 110,
  },
  {
    key: 'is_visible',
    label: 'Visible',
    group: 'status',
    kind: 'bool',
    target: { column: 'is_visible' },
    defaultVisible: true,
    width: 80,
  },
  {
    key: 'is_featured',
    label: 'Featured',
    group: 'status',
    kind: 'bool',
    target: { column: 'is_featured' },
    defaultVisible: false,
    width: 80,
  },
  {
    key: 'tax_exempt',
    label: 'Tax exempt',
    group: 'status',
    kind: 'bool',
    target: { column: 'tax_exempt' },
    defaultVisible: false,
    width: 95,
  },
  {
    key: 'exclude_from_platform_pool',
    label: 'Keep out of pool',
    group: 'status',
    kind: 'bool',
    target: { column: 'exclude_from_platform_pool' },
    defaultVisible: false,
    width: 120,
  },

  {
    key: 'bid_count',
    label: 'Bids',
    group: 'live',
    kind: 'readonly',
    target: { column: 'bid_count' },
    defaultVisible: true,
    width: 60,
  },
  {
    key: 'current_bid_cents',
    label: 'Current bid',
    group: 'live',
    kind: 'readonly',
    target: { column: 'current_bid_cents' },
    defaultVisible: true,
    width: 110,
  },
  {
    key: 'ends_at',
    label: 'Closes',
    group: 'live',
    kind: 'readonly',
    target: { column: 'ends_at' },
    defaultVisible: false,
    width: 170,
  },
  {
    key: 'added_in_warehouse_at',
    label: 'From warehouse',
    group: 'live',
    kind: 'readonly',
    target: { column: 'added_in_warehouse_at' },
    defaultVisible: false,
    width: 130,
  },
];

/**
 * "Reviewed" — the far-left column (Jim, 2026-10-06: "a 'Reviewed' column with
 * a checkbox so the auction house knows when a lot is reviewed and ready… It
 * should be the far left column"). It is NOT in `LOT_GRID_COLUMNS`: the grid
 * pins it beside the photo rather than scrolling it, so it is always in view.
 *
 * The tick is the only part a person writes. When, who and their initials are
 * the server's (`trg_lots_reviewed`), which is why nothing here can edit them.
 */
export const REVIEWED_COLUMN: GridColumn = {
  key: 'reviewed',
  label: 'Reviewed',
  group: 'status',
  kind: 'bool',
  target: { column: 'reviewed' },
  defaultVisible: true,
  width: 92,
};

/** Every column the grid knows, pinned ones included — for lookups. */
export const ALL_GRID_COLUMNS: readonly GridColumn[] = [REVIEWED_COLUMN, ...LOT_GRID_COLUMNS];

export const GRID_GROUP_LABEL: Record<GridGroup, string> = {
  identity: 'Identity',
  pricing: 'Pricing',
  listing: 'Show on listing',
  specs: 'Specs',
  identifiers: 'Identifiers',
  location: 'Location',
  status: 'Status',
  live: 'Bidding (read-only)',
};

export const gridColumn = (key: string): GridColumn => {
  const found = ALL_GRID_COLUMNS.find((c) => c.key === key);
  if (!found) throw new Error(`unknown grid column: ${key}`);
  return found;
};

/** A lot nobody can bid on yet — prices and ordering are free to move. */
/**
 * What the Reviewed cell says beside its tick: the initials the server stamped,
 * or nothing. A tick that has not been saved yet has no initials — they are
 * filled in when it is.
 */
export function reviewedBy(lot: Pick<LotRow, 'reviewed' | 'reviewed_by_initials'>): string {
  return lot.reviewed ? (lot.reviewed_by_initials ?? '') : '';
}

/** How many of these lots the office has ticked off. */
export function reviewedCount(lots: Pick<LotRow, 'reviewed'>[]): number {
  return lots.filter((l) => l.reviewed).length;
}

export function lotHasBids(lot: Pick<LotRow, 'bid_count'>): boolean {
  return (lot.bid_count ?? 0) > 0;
}

/** Is this lot open to bidders right now? Used only to confirm a reserve change. */
export function lotIsLive(lot: Pick<LotRow, 'status'>): boolean {
  return lot.status === 'active' || lot.status === 'closing';
}

/**
 * Why this cell can't be edited, in words for the person — or null when it can.
 * See the file header for the reasoning behind each one.
 */
export function lockedReason(
  lot: Pick<LotRow, 'bid_count' | 'status'>,
  column: GridColumn,
): string | null {
  if (column.kind === 'readonly') return 'This is what bidding has done — it is not typed in.';
  if (!lotHasBids(lot)) return null;
  switch (column.key) {
    case 'starting_bid_cents':
      return 'This lot has bids. The starting bid sets the increments, so changing it now would move the ground under bids already placed — cancel the lot and relist it instead.';
    case 'lot_number':
      return 'This lot has bids. Its number is how bidders found it.';
    case 'sale_order':
      return 'This lot has bids. The order decides when it closes, and people are bidding against that clock.';
    default:
      return null;
  }
}

/** The six prices, for the draft-review door. */
export const GRID_PRICE_COLUMNS: Readonly<Record<string, PriceKey>> = {
  msrp_cents: 'msrp_cents',
  retail_price_cents: 'retail_price_cents',
  starting_bid_cents: 'starting_bid_cents',
  reserve_cents: 'reserve_cents',
  estimate_min_cents: 'estimate_min_cents',
  estimate_max_cents: 'estimate_max_cents',
};

const specValue = (lot: Pick<LotRow, 'custom_fields'>, key: string): unknown => {
  const specs = lot.custom_fields;
  if (typeof specs !== 'object' || specs === null || Array.isArray(specs)) return undefined;
  return (specs as Record<string, unknown>)[key];
};

/**
 * A lot value as cell text. Some lot columns are JSON or arrays, so this is
 * narrow on purpose: anything that is not a plain scalar shows as empty rather
 * than as "[object Object]".
 */
const asText = (v: unknown): string =>
  typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? String(v) : '';

/** Cents as a plain editable number — "11.95", never "$11.95" and never "0.00" for nothing. */
export const centsToCell = (cents: number | null | undefined): string =>
  cents === null || cents === undefined ? '' : (cents / 100).toFixed(2);

/**
 * What a cell shows when nobody is editing it. Booleans are handled by the
 * page (a tick box), so this is the text of everything else.
 */
export function cellText(lot: LotRow, column: GridColumn): string {
  if ('customField' in column.target) {
    return asText(specValue(lot, column.target.customField));
  }
  const raw = lot[column.target.column];
  if (raw === null || raw === undefined) return '';
  if (column.kind === 'money') return centsToCell(raw as number);
  return asText(raw);
}

export type CellParse =
  | { ok: true; value: string | number | boolean | null }
  | { ok: false; message: string };

/**
 * What a typed cell means, or why it is not acceptable. A blank clears the
 * field — except where the lot cannot be without one, where it is refused
 * rather than quietly turned into a 0 or a 1.
 */
export function parseCell(column: GridColumn, raw: string): CellParse {
  const text = raw.trim();
  switch (column.kind) {
    case 'money': {
      const cents = parseDollarsInput(text);
      if (cents === undefined) return { ok: false, message: 'Enter an amount like 25 or 25.50.' };
      if (cents === null) {
        if (column.key === 'starting_bid_cents') {
          return { ok: false, message: 'Every lot needs a starting bid.' };
        }
        return { ok: true, value: null };
      }
      return { ok: true, value: cents };
    }
    case 'int': {
      if (text === '') {
        if (column.key === 'sale_order') return { ok: true, value: null };
        return { ok: false, message: `${column.label} can’t be empty.` };
      }
      if (!/^\d+$/.test(text)) return { ok: false, message: 'Enter a whole number.' };
      const n = Number(text);
      if (column.key !== 'sale_order' && n < 1) {
        return { ok: false, message: `${column.label} must be 1 or more.` };
      }
      return { ok: true, value: n };
    }
    case 'select': {
      if (column.key === 'status') {
        if (!(GRID_LOT_STATUSES as readonly string[]).includes(text)) {
          return { ok: false, message: 'Pick a status from the list.' };
        }
        return { ok: true, value: text };
      }
      return { ok: true, value: text === '' ? null : text };
    }
    case 'bool':
      return { ok: true, value: text === 'true' };
    case 'readonly':
      return { ok: false, message: 'This cell is read-only.' };
    default:
      return { ok: true, value: text === '' ? null : text };
  }
}

/** One lot's pending edits: grid column key → the value `parseCell` gave. */
export type RowEdits = Record<string, string | number | boolean | null>;

/**
 * A row's edits as a patch for `lots`. Specs are merged into the lot's own
 * `custom_fields` — a spec the person emptied is REMOVED rather than stored as
 * an empty string, so a blank cell and a missing spec mean the same thing.
 */
export function rowPatch(lot: LotRow, edits: RowEdits): TablesUpdate<'lots'> {
  const patch: Record<string, unknown> = {};
  let specs: Record<string, unknown> | null = null;
  for (const [key, value] of Object.entries(edits)) {
    const column = gridColumn(key);
    if ('customField' in column.target) {
      if (specs === null) {
        const current = lot.custom_fields;
        specs =
          typeof current === 'object' && current !== null && !Array.isArray(current)
            ? { ...(current as Record<string, unknown>) }
            : {};
      }
      if (value === null || value === '') delete specs[column.target.customField];
      else specs[column.target.customField] = value;
      continue;
    }
    patch[column.target.column as string] = value;
  }
  if (specs !== null) patch.custom_fields = specs;
  return patch as TablesUpdate<'lots'>;
}

/** Has this cell actually been changed, or typed back to what it already was? */
export function cellChanged(lot: LotRow, column: GridColumn, value: unknown): boolean {
  if (column.kind === 'bool') {
    const current =
      'customField' in column.target
        ? specValue(lot, column.target.customField)
        : lot[column.target.column];
    return Boolean(current) !== Boolean(value);
  }
  if ('customField' in column.target) {
    const current = specValue(lot, column.target.customField);
    const now = value === null || value === '' ? '' : asText(value);
    return asText(current) !== now;
  }
  const current = lot[column.target.column];
  if (current === null || current === undefined) return value !== null && value !== '';
  return asText(current) !== asText(value ?? '');
}

/**
 * The reserve changes the page must ask about before writing: a live lot whose
 * reserve is moving. Bidders are in the room for those.
 */
export function liveReserveChanges(lots: LotRow[], edits: Map<string, RowEdits>): number {
  let n = 0;
  for (const lot of lots) {
    const row = edits.get(lot.id);
    if (!row) continue;
    if ('reserve_cents' in row && lotIsLive(lot)) n += 1;
  }
  return n;
}

/** "3 changes on 2 lots" — what the Save button counts. */
export function countEdits(edits: Map<string, RowEdits>): { cells: number; lots: number } {
  let cells = 0;
  let lots = 0;
  for (const row of edits.values()) {
    const n = Object.keys(row).length;
    if (n === 0) continue;
    cells += n;
    lots += 1;
  }
  return { cells, lots };
}

/**
 * The AI's suggested prices for a lot, as pending edits — what "Fill in AI
 * prices" stages so a sale's worth of research can be eyeballed and saved in
 * one go, instead of a lot page each. A suggestion the lot already matches is
 * left out, so the count is honest about what would change.
 */
export function aiPriceEdits(
  lot: LotRow,
  suggested: Partial<Record<PriceKey, number | null>>,
): RowEdits {
  const row: RowEdits = {};
  for (const [key, price] of Object.entries(GRID_PRICE_COLUMNS)) {
    const cents = suggested[price];
    if (cents === null || cents === undefined) continue;
    const column = gridColumn(key);
    if (lockedReason(lot, column) !== null) continue;
    if (cellChanged(lot, column, cents)) row[key] = cents;
  }
  return row;
}

// ─── Column widths the person drags ─────────────────────────────────────────

/** Narrow enough to be useful, wide enough to still be a column. */
export const MIN_COLUMN_WIDTH = 48;
export const MAX_COLUMN_WIDTH = 900;

export const clampColumnWidth = (px: number): number =>
  Math.max(MIN_COLUMN_WIDTH, Math.min(MAX_COLUMN_WIDTH, Math.round(px)));

/**
 * Dragged widths out of the browser's storage, read defensively: a key that is
 * not a column any more, or a width that is not a usable number, is dropped
 * rather than breaking the grid for someone whose saved layout has gone stale.
 */
export function parseStoredWidths(raw: unknown): Record<string, number> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!ALL_GRID_COLUMNS.some((c) => c.key === key)) continue;
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    out[key] = clampColumnWidth(value);
  }
  return out;
}

/** Which prices a row's edits touch — the ones a pending AI draft must be told about. */
export function editedPrices(edits: RowEdits): PriceKey[] {
  const out: PriceKey[] = [];
  for (const key of Object.keys(edits)) {
    const price = GRID_PRICE_COLUMNS[key];
    if (price) out.push(price);
  }
  return out;
}
